import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

const About: React.FC = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';

    const [currentSlide, setCurrentSlide] = useState(0);
    const slides = [
        {
            title: t('aboutPage.slider.visionTitle'),
            content: t('aboutPage.slider.visionText')
        },
        {
            title: t('aboutPage.slider.missionTitle'),
            content: t('aboutPage.slider.missionText')
        },
        {
            title: t('aboutPage.slider.goalsTitle'),
            content: t('aboutPage.slider.goalsText')
        }
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % slides.length);
        }, 5000);
        return () => clearInterval(timer);
    }, [slides.length]);

    const fadeInUp = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
    };

    return (
        <div className="bg-gradient-to-b from-white to-red-50 min-h-screen">
            {/* Hero Section */}
            <div className="relative h-[400px] overflow-hidden flex items-center justify-center bg-primary">
                <div className="absolute inset-0 opacity-30">
                    <img
                        src="/images/about-header.png"
                        alt="Promoshop Products"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="relative z-10 text-center px-4">
                    <motion.h1
                        className="text-5xl md:text-6xl font-bold text-white mb-4"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        {t('aboutPage.heroTitle')}
                    </motion.h1>
                    <motion.p
                        className="text-xl text-green-100 max-w-2xl mx-auto"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        {t('aboutPage.heroSubtitle')}
                    </motion.p>
                </div>
            </div>

            <div className="container mx-auto px-4 py-16">
                {/* Section 1: The Beginning */}
                <motion.div
                    className="flex flex-col md:flex-row items-center gap-12 mb-24"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={fadeInUp}
                >
                    <div className="w-full md:w-1/2">
                        <div className="relative rounded-3xl overflow-hidden shadow-elevated aspect-[4/3]">
                            <img
                                src="https://images.unsplash.com/photo-1540555700478-4be289fbecef?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                                alt="Our Beginning"
                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                            />
                        </div>
                    </div>
                    <div className="w-full md:w-1/2">
                        <h2 className="text-3xl font-bold text-primary mb-6">
                            {t('aboutPage.storyTitle')}
                        </h2>
                        <div className="prose prose-lg text-neutral-600">
                            <p className="mb-4">
                                {t('aboutPage.storyText1')}
                            </p>
                            <p>
                                {t('aboutPage.storyText2')}
                            </p>
                        </div>
                    </div>
                </motion.div>

                {/* Section 2: Our Values */}
                <motion.div
                    className="flex flex-col md:flex-row-reverse items-center gap-12 mb-24"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={fadeInUp}
                >
                    <div className="w-full md:w-1/2">
                        <div className="relative rounded-3xl overflow-hidden shadow-elevated aspect-[4/3]">
                            <img
                                src="https://images.unsplash.com/photo-1615486511484-92e172cc4fe0?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                                alt="Our Values"
                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                            />
                        </div>
                    </div>
                    <div className="w-full md:w-1/2">
                        <h2 className="text-3xl font-bold text-primary mb-6">
                            {t('aboutPage.valuesTitle')}
                        </h2>
                        <div className="prose prose-lg text-neutral-600">
                            <p className="mb-4">
                                {t('aboutPage.valuesText1')}
                            </p>
                            <p>
                                {t('aboutPage.valuesText2')}
                            </p>
                        </div>
                    </div>
                </motion.div>

                {/* Section 3: The Vision (Slider) */}
                <motion.div
                    className="text-center max-w-4xl mx-auto bg-white rounded-3xl p-12 shadow-card relative min-h-[300px] flex flex-col justify-center"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={fadeInUp}
                >
                    <div className="relative overflow-hidden w-full">
                        <AnimatePresence mode='wait'>
                            <motion.div
                                key={currentSlide}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.5 }}
                                className="w-full"
                            >
                                <h2 className="text-3xl font-bold text-primary mb-6">
                                    {slides[currentSlide].title}
                                </h2>
                                <p className="text-xl text-neutral-600 leading-relaxed mb-8">
                                    {slides[currentSlide].content}
                                </p>
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    <div className="flex justify-center gap-4 mt-8">
                        {slides.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentSlide(index)}
                                className={`h-1 rounded-full transition-all duration-300 ${currentSlide === index ? 'w-16 bg-primary' : 'w-4 bg-primary/30 hover:bg-primary/50'
                                    }`}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default About;
